import type { GepQueuedEvent } from "./types";

export const QUEUE_STORAGE_KEY = "_gep_queue";
const DEFAULT_MAX_QUEUE_SIZE = 200;

function storage(): Storage | undefined {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    return undefined;
  }
  return undefined;
}

export class GepEventQueue {
  private events: GepQueuedEvent[] = [];
  private readonly maxSize: number;

  constructor(maxSize: number = DEFAULT_MAX_QUEUE_SIZE) {
    this.maxSize = maxSize;
    this.load();
  }

  load(): void {
    let raw: string | null = null;
    try {
      raw = storage()?.getItem(QUEUE_STORAGE_KEY) ?? null;
    } catch {
      // localStorage can be blocked (SecurityError). The tracker is
      // constructed at module init, so a throwing read must not stop app boot:
      // continue with an empty in-memory queue.
      return;
    }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.events = parsed.filter(
          (event): event is GepQueuedEvent =>
            !!event &&
            typeof event.id === "string" &&
            typeof event.name === "string",
        );
      }
    } catch {
      this.events = [];
    }
  }

  persist(): void {
    try {
      storage()?.setItem(QUEUE_STORAGE_KEY, JSON.stringify(this.events));
    } catch {
      // ignore quota / unavailable storage
    }
  }

  push(event: GepQueuedEvent): void {
    this.events.push(event);
    if (this.events.length > this.maxSize) {
      this.events.splice(0, this.events.length - this.maxSize);
    }
    this.persist();
  }

  take(batchSize: number): GepQueuedEvent[] {
    return this.events.slice(0, batchSize);
  }

  size(): number {
    return this.events.length;
  }

  has(eventId: string): boolean {
    return this.events.some((event) => event.id === eventId);
  }

  remove(eventIds: string[]): void {
    const ids = new Set(eventIds);
    this.events = this.events.filter((event) => !ids.has(event.id));
    this.persist();
  }

  clear(): void {
    this.events = [];
    this.persist();
  }

  /**
   * Drops events matching `predicate` and persists the result.
   */
  dropWhere(predicate: (event: GepQueuedEvent) => boolean): void {
    const next = this.events.filter((event) => !predicate(event));
    if (next.length !== this.events.length) {
      this.events = next;
      this.persist();
    }
  }

  /**
   * Drops events queued at or before `iso` (ISO-8601). Used to discard events
   * that predate a consent revocation, even when the queue purge could not be
   * persisted (storage unwritable) and the events reload on the next boot.
   */
  dropBefore(iso: string): void {
    if (!iso) return;
    this.events = this.events.filter((event) => (event.timestamp || "") > iso);
    this.persist();
  }
}
